import proj4 from "proj4";
import {transform} from 'ol/proj';
import {Projection, Transform} from '../../redux/projections-slice';
import PCA from 'pca-js';
import { executeWithoutLogging } from "./utilities";
import {
  Euler,
  MathUtils,
  Matrix4,
  Quaternion,
  Sphere,
  Vector3
} from 'three';

const unitsToValue = {
  "m": 1.0,
  "ft": 0.3048,
  "us-ft": 1200.0 / 3937.0
};

const lonLatProjection = {
  type: "proj4",
  name: "latlong",
  string: "+proj=longlat +ellps=WGS84 +datum=NAD83 +no_defs"
} as Projection;

const verifyProjection = (info) => {
  const data = {
    type: info.type,
    name: info.name,
    string: info.string,
    transform: info.transform
  } as Projection;

  if (data.type === "proj4") {
    data.string =  verifyProj4String(data);
  }

  data.transform = verifyTransformObject(data);

  return data;
};

/* Mostly used indirectly inside other coordinate type */
class ScenePointConverter {
  protected _sceneShift;
  protected _sceneScale;
  protected _dataProjection: Projection;
  protected _viewProjection: Projection;

  get origin() {
    return this._sceneShift;
  }

  get initialized() {
    return this._sceneShift !== null;
  }

  /** Scale from data projection to meters (local scene coordinates) */
  get dataToMeters() {
    return infoToMeters(this._dataProjection);
  }

  get dataProjection() {
    return this._dataProjection;
  }

  get viewProjection() {
    return this._viewProjection;
  }

  get sceneOffset() {
    return this._sceneShift;
  }

  get sceneScale() {
    return this._sceneScale;
  }

  get dataProjectionName() {
    return this._dataProjection.name;
  }

  get dataProjectionUnits() {
    return projectionUnits(this._dataProjection);
  }

  get viewProjectionUnits() {
    return projectionUnits(this._viewProjection);
  }

  get isLonLatProject() {
    return isLonLatProjection(this._dataProjection);
  }

  engineStyleProjection(projection: Projection) {
    const proj4String = projection.string;
    const hasTransform = projection.transform ? true : false;

    let results;
    if (hasTransform === false) {
      // Standard projection
      results = proj4String;
    } else {
      // Projection with transform (engine style projections are saved differently)
      const data = projection.transform;
      const origin = data.origin;
      const translation = data.translation;
      const rotation = data.rotation;
      const scale = data.scale;

      const transform = {
        origin_east: origin[0],
        origin_north: origin[1],
        origin_elevation: origin[2],
        translation_east: translation[0],
        translation_north: translation[1],
        translation_elevation: translation[2],
        rotation: rotation,
        scale_factor_h: scale,
        scale_factor_v: 1.0,
      };

      results = [proj4String, transform];
    }

    const projectionString = JSON.stringify(results);
    return projectionString;
  }

  currentProjection(name) {
    return (this._dataProjection.name === name) ||
      (this._viewProjection.name === name);
  }

  setDataProjection(info) {
    this._dataProjection = verifyProjection(info);
    this.resetSceneShift();
  }

  setViewProjection(info) {
    this._viewProjection = verifyProjection(info);
  }

  resetSceneShift() {
    this._sceneShift = null;
    this._sceneScale = new Vector3(1.0, 1.0, 1.0);
  }

  checkForOffset(offset) {
    if (this._sceneShift === null) {
      this._sceneShift = offset.clone();

      if (this.isLonLatProject) {
        // Create an approximate latlon --> meters mapping based on the
        // offset point. Be aware that the accuracy is worse the further
        // away from the central point that you go

        let dx = haversineDistance(
          new Vector3(offset.x, offset.y, 0),
          new Vector3(offset.x + 1, offset.y, 0)
        );

        let dy = haversineDistance(
          new Vector3(offset.x, offset.y, 0),
          new Vector3(offset.x, offset.y + 1, 0)
        );

        this._sceneScale = new Vector3(dx, dy, 1.0);
      } else {
        this._sceneScale = new Vector3(1.0, 1.0, 1.0);
      }
    }
  }

  offsetToScene(offset) {
    this.checkForOffset(offset);
    let difference = new Vector3()
      .add(offset)
      .sub(this._sceneShift);

    return difference;
  }

  /** @private Convert from data projection to lonlat */
  _dataToLonLat(value) {
    return new ProjectedCoordinate(value, this._dataProjection).toLonLat();
  }

  /** @private Convert from data projection to aerial coordinates */
  _dataToAerial(value) {
    let positionLonLat = this._dataToLonLat(value);
    return transform([positionLonLat.x, positionLonLat.y],
      'EPSG:4326', 'EPSG:3857');
  }

  /** @private Convert from scene coordinates (meters) to data projection */
  _sceneToData(value) {
    let position = new Vector3().copy(value);

    if (this.isLonLatProject) {
      position.divide(this._sceneScale);
    }

    position.add(this.origin)
      .divideScalar(this.dataToMeters);

    return position;
  }

  /** @private Convert from data projection to scene coordinates (meters) */
  _dataToScene(value) {
    let position = new Vector3()
      .copy(value)
      .multiplyScalar(this.dataToMeters);

    this.checkForOffset(position);
    position.sub(this.origin);

    if (this.isLonLatProject) {
      position.multiply(this._sceneScale);
    }

    return new SceneCoordinate(position);
  }

  /** @private Convert from scene coordinates (meters) to web mercator */
  _sceneToAerial(value) {
    let dataProjection = this._sceneToData(value);
    return this._dataToAerial(dataProjection);
  }

  /** @private Convert from scene coordinates (meters) to lonlat */
  _sceneToLonLat(value) {
    let dataProjection = this._sceneToData(value);
    return this._dataToLonLat(dataProjection);
  }

  /** @private */
  _sceneToGeocentric(value) {
    let dataLonLat = this._sceneToLonLat(value);
    return new GeocentricCoordinate().fromLonLat(dataLonLat);
  }

  /** @private */
  _geocentricToScene(value) {
    let pointData = new GeocentricCoordinate(value).toProjection(this._dataProjection);
    return this._dataToScene(pointData);
  }
}

/** Base Coordinate class is Vector3 with a few extras */
class Coordinate extends Vector3 {
  constructor(value = null) {
    let x = 0;
    let y = 0;
    let z = 0;

    if (value !== null) {
      if (value.isVector3 || (value instanceof Coordinate)) {
        x = value.x;
        y = value.y;
        z = value.z;
      } else {
        x = value[0];
        y = value[1];
        z = (value.length === 3) ? value[2] : 0;
      }
    }

    super(x,y,z);
  }

  get vector() {
    return new Vector3(this.x, this.y, this.z);
  }

  clone() {
    return new Vector3().copy(this);
  }
}

/** Coordinate in scene coordinates */
export class SceneCoordinate extends Coordinate {
  constructor(value = null) {
    super(value);
  }

  toAerial() {
    return LocalScene._sceneToAerial(this);
  }

  toLonLat() {
    return LocalScene._sceneToLonLat(this);
  }

  toDataProjection() {
    const value = LocalScene._sceneToData(this);
    return new DataProjectionCoordinate(value);
  }

  toViewProjection() {
    return this.toDataProjection().toViewProjection();
  }
}

/** Coordinate in geocentric */
export class GeocentricCoordinate extends Coordinate {
  constructor(value = null) {
    super(value);
  }

  get closestUtmZone() {
    let lonlat = this.toLonLat();
    return Math.ceil((180 + lonlat.x) / 6);
  }

  fromScene(value) {
    return LocalScene._sceneToGeocentric(value);
  }

  fromLonLat(value) {
    if (!value.isVector3) {
      value = new Vector3(...value);
    }

    let lon = value.x * Math.PI / 180.0;
    let lat = value.y * Math.PI / 180.0;
    let alt = value.z; // must be in meters!

    let a = 6378137; // wgs84
    let f = 1 / 298.257223563; // wgs84

    let b = a * (1 - f);
    let e = Math.sqrt((Math.pow(a, 2) - Math.pow(b, 2)) / Math.pow(a, 2));

    let N = a / Math.sqrt(1 - Math.pow(e, 2) * Math.pow(Math.sin(lat), 2));
    let x = (N + alt) * Math.cos(lat) * Math.cos(lon);
    let y = (N + alt) * Math.cos(lat) * Math.sin(lon);
    let z = (N * (Math.pow(b, 2) / Math.pow(a, 2)) + alt) * Math.sin(lat);

    this.x = x;
    this.y = y;
    this.z = z;

    return new GeocentricCoordinate([x, y, z]);
  }

  toDataProjection() {
    const value = this.toProjection(LocalScene.dataProjection);
    return new DataProjectionCoordinate(value);
  }

  toViewProjection() {
    const value = this.toProjection(LocalScene.viewProjection);
    return new ViewProjectionCoordinate(value);
  }

  toScene() {
    return LocalScene._geocentricToScene(this);
  }

  toLonLat() {
    let x = this.x * 1.0;
    let y = this.y * 1.0;
    let z = this.z * 1.0;

    let a = 6378137; // wgs84
    let f = 1 / 298.257223563; // wgs84

    let b = a * (1 - f);
    let e = Math.sqrt((Math.pow(a, 2) - Math.pow(b, 2)) / Math.pow(a, 2));
    b = Math.sqrt(Math.pow(a, 2) * (1 - Math.pow(e, 2)));

    let ep = Math.sqrt((Math.pow(a, 2) - Math.pow(b, 2)) / Math.pow(b, 2));
    let p = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
    let th = Math.atan2(a * z, b * p);
    let lon = Math.atan2(y, x);
    let lat = Math.atan2((z + Math.pow(ep, 2) * b * Math.pow(Math.sin(th), 3)),
      (p - Math.pow(e, 2) * a * Math.pow(Math.cos(th), 3)));
    let N = a / (Math.sqrt(1 - Math.pow(e, 2) * Math.pow(Math.sin(lat), 2)));
    let alt = p / Math.cos(lat) - N;
    lon = lon % (2 * Math.PI);

    if (Math.abs(x) < 1.0 && Math.abs(y) < 1.0) {
      alt = Math.abs(z) - b;
    }

    lon = lon * 180.0 / Math.PI;
    lat = lat * 180.0 / Math.PI;

    return new Vector3(lon, lat, alt);
  }

  toProjection(projection: Projection) {
    let lonlat = this.toLonLat();
    let vector = _proj4Transform(lonlat, lonLatProjection, projection);

    if (projection.transform) {
      var origin = new Vector3(...projection.transform.origin);
      var trans = new Vector3(...projection.transform.translation);
      var scale = projection.transform.scale;
      var scaleVector = new Vector3(scale, scale, 1.0);
      var angle = projection.transform.rotation * Math.PI / 180.0;
      let axis = new Vector3(0, 0, 1);

      vector = vector.sub(origin)
        .multiply(scaleVector)
        .applyAxisAngle(axis, angle)
        .add(trans);
    }

    return new ProjectedCoordinate(vector, projection);
  }
}

/** Coordinate in generic projection */
export class ProjectedCoordinate extends Coordinate {
  public projection: Projection;

  constructor(value, projection) {
    super(value);
    this.projection = projection;
  }

  toGeocentric() {
    const lonLat = this.toLonLat();
    return new GeocentricCoordinate().fromLonLat(lonLat);
  }

  toLonLat() {
    const transform = this.projection.transform;

    let vector;
    if (transform) {
      let origin = new Vector3(...transform.origin);
      let trans = new Vector3(...transform.translation);
      let scale = transform.scale;
      let scaleVector = new Vector3(scale, scale, 1.0);
      let angle = transform.rotation * Math.PI / 180.0;
      let axis = new Vector3(0, 0, 1);

      vector = new Vector3()
        .add(this)
        .sub(trans)
        .applyAxisAngle(axis, -1 * angle)
        .divide(scaleVector)
        .add(origin);
    } else {
      vector = this;
    }

    let lonlat = _proj4Transform(vector, this.projection, lonLatProjection);
    return lonlat;
  }

  toAerial() {
    let value = this.projection.name !== LocalScene.dataProjection.name
      ? this.toProjection(LocalScene.dataProjection)
      : this;

    return LocalScene._dataToAerial(value);
  }

  toScene() {
    let value = this.projection.name !== LocalScene.dataProjection.name
      ? this.toProjection(LocalScene.dataProjection)
      : this;

    return LocalScene._dataToScene(value);
  }

  toProjection(projection) {
    let vector = _proj4Transform(this, this.projection, projection);
    return new ProjectedCoordinate(vector, projection);
  }

  toViewProjection() {
    return new ViewProjectionCoordinate(this.toProjection(LocalScene.viewProjection));
  }

  toDataProjection() {
    return new DataProjectionCoordinate(this.toProjection(LocalScene.viewProjection));
  }
  distanceTo(value) {
    if (value instanceof ProjectedCoordinate) {
      let isLonLat1 = isLonLatProjection(this.projection);
      let isLonLat2 = isLonLatProjection(value.projection);
      if (isLonLat1 && isLonLat2) {
        return haversineDistance(this, value);
      }

      return this.vector.distanceTo(value.vector);
    }

    return this.vector.distanceTo(value);
  }
}

/** Coordinate in project data projection */
export class DataProjectionCoordinate extends ProjectedCoordinate  {
  constructor(value = null) {
    super(value, LocalScene.dataProjection);
  }
}

/** Coordinate in project view projection */
export class ViewProjectionCoordinate extends ProjectedCoordinate  {
  constructor(value = null) {
    super(value, LocalScene.viewProjection);
  }
}

/** Spherical position, with theta/phi in degrees */
export class SphericalPosition {
  public radius;
  private _theta;
  private _phi;
  private limits;

  constructor() {
    this.radius = 0;
    this._theta = 0;
    this._phi = 0;
    this.limits = [0.01, 179.9];
  }

  get theta() {
    return (this._theta + 360) % 360;
  }

  get phi() {
    return (this._phi + 360) % 360;
  }

  round(value, precision) {
    let divisor = Math.pow(10, precision);
    return Math.round(value * divisor) / divisor;
  }

  fromVectors(pivot, orbit) {
    let offset = new Vector3().add(orbit).sub(pivot);
    let radius = offset.length();

    let theta = Math.atan2(offset.y, offset.x);
    theta = MathUtils.radToDeg(theta);

    let dist2d = Math.sqrt(Math.pow(offset.x, 2) + Math.pow(offset.y, 2));
    let phi = Math.atan2(dist2d, offset.z);
    phi = MathUtils.radToDeg(phi);
    phi = Math.max(this.limits[0], phi);
    phi = Math.min(this.limits[1], phi);

    this._theta = this.round(theta, 3);
    this._phi = this.round(phi, 3);
    this.radius = this.round(radius, 3);

    return this;
  }

  fromValues(radius, theta, phi) {
    this.radius = radius;
    this._theta = theta;
    this._phi = phi;

    return this;
  }

  toVector3() {
    let theta = MathUtils.degToRad(this.theta);
    let phi = MathUtils.degToRad(this.phi);

    let x = this.radius * Math.cos(theta) * Math.sin(phi);
    let y = this.radius * Math.sin(theta) * Math.sin(phi);
    let z = this.radius * Math.cos(phi);

    return new Vector3(x, y, z);
  }
}

/* Utility functions */
export const isValidProjection = (projectionString) => {
  // Check for valid projection string
  try {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let valid = proj4(projectionString);
    return true;
  } catch {
    return false;
  }
};

export const getInvertedEuler = (euler) => {
  let inverseQuaternion = new Quaternion()
    .setFromEuler(euler)
    .invert();

  let eulerOriginalInverse = new Euler()
    .setFromQuaternion(inverseQuaternion);

  return eulerOriginalInverse;
};

export const geometryToArray = (geometry, projection = null) => {
  let polygon = [];
  let coordinates = geometry.flatCoordinates;
  let totalPoints = coordinates.length / 2.0;

  for (var i = 0; i < totalPoints; i++) {
    let x = coordinates[2 * i + 0];
    let y = coordinates[2 * i + 1];
    let lonlat = transform([x, y, 0], 'EPSG:3857', 'EPSG:4326');
    let ecef = new GeocentricCoordinate().fromLonLat(lonlat);
    if (projection) {
      let point = ecef.toProjection(projection);
      polygon.push(point.toArray());
    } else {
      polygon.push(ecef.toArray());
    }
  }

  return polygon;
};

export const calculatePointsPCA = (vertices, returnFlat) => {
  let mean = new Vector3();
  vertices.forEach(vertex => {
    mean.add(vertex);
  });
  mean.divideScalar(vertices.length);

  let points = vertices.map(vertex => {
    return new Vector3()
      .add(vertex)
      .sub(mean)
      .toArray();
  });

  let eigen = executeWithoutLogging(() => {
    return PCA.getEigenVectors(points);
  });

  let matrix = new Matrix4();
  matrix.makeBasis(
    new Vector3().fromArray(eigen[0].vector),
    new Vector3().fromArray(eigen[1].vector),
    new Vector3().fromArray(eigen[2].vector)
  );
  matrix.transpose();

  points = points.map(point => {
    return new Vector3()
      .fromArray(point).applyMatrix4(matrix);
  });

  if (returnFlat) {
    let pointsFlat = [];
    points.forEach(point => {
      pointsFlat = [...pointsFlat, ...point.toArray()];
    });
    points = pointsFlat;
  }

  return points;
};

export const projectionUnits = (projection: Projection) => {
  const unitLength = infoToMeters(projection);

  let units;
  switch (unitLength) {
  case unitsToValue['m']:
    units = "m";
    break;
  case unitsToValue['ft']:
    units = "ft";
    break;
  case unitsToValue['us-ft']:
    units = "sft";
    break;
  default:
    units = "m";
    break;
  }

  return units;
};

export const infoToMeters = (projection: Projection) => {
  if (!projection) {
    return 1.0;
  }

  const proj = proj4.Proj(projection.string);

  if (projection.type === "wkt") {
    return proj['UNIT'].convert;
  }

  if ("to_meter" in proj) {
    return proj['to_meter'];
  }

  if ("units" in proj) {
    const units = proj['units'];
    return unitsToValue[units];
  }

  return 1.0;
};

// NOTE: Will fail if converted to an es6 function
export function sphereRaycast(raycaster, intersects) {
  let geometry = this.geometry;
  let matrixWorld = this.matrixWorld;

  let testSphere = new Sphere();
  testSphere.copy(geometry.boundingSphere);
  testSphere.applyMatrix4(matrixWorld);

  if (raycaster.ray.intersectsSphere(testSphere) === false) {
    return;
  }

  let origin = raycaster.ray.origin;
  let distance = origin.distanceTo(this.position);

  intersects.push({
    distance: distance,
    point: null,
    object: this
  });
}

export function isLonLatProjection(projection) {
  const proj = proj4.Proj(projection.string);
  return proj['projName'] === "longlat";
}

function haversineDistance(position1, position2) {
  let earthRadius = 6371000;

  let diffLat = (position2.y - position1.y) * Math.PI / 180;
  let diffLng = (position2.x - position1.x) * Math.PI / 180;

  let arc = Math.cos(position1.y * Math.PI / 180)
    * Math.cos(position2.y * Math.PI / 180)
    * Math.sin(diffLng / 2) * Math.sin(diffLng / 2)
    + Math.sin(diffLat / 2) * Math.sin(diffLat / 2);
  let line = 2 * Math.atan2(Math.sqrt(arc), Math.sqrt(1 - arc));

  let distance2d = earthRadius * line;
  let height = position2.z - position1.z;

  let distance = Math.pow(distance2d, 2) + Math.pow(height, 2);
  distance = Math.sqrt(distance);

  return distance;
}

function verifyTransformObject(data) {
  let transform = data.transform;
  if (!transform) {
    return null;
  }

  if (transform.hasOwnProperty('translation_north')) {
    return {
      origin: [
        transform.origin_east,
        transform.origin_north,
        transform.origin_elevation ? transform.origin_elevation : 0
      ],
      translation: [
        transform.translation_east,
        transform.translation_north,
        transform.translation_elevation
      ],
      scale: transform.scale_factor_h,
      rotation: transform.rotation
    } as Transform;
  }

  return Object.assign({}, transform);
}

function verifyProj4String(projection) {
  let string = projection.string;

  // longlat projection is returned directly
  if (isLonLatProjection(projection)) {
    return string;
  }

  // Return EPSG codes directly
  if (string.toUpperCase().indexOf("EPSG") === 0) {
    return string;
  }

  // Add missing fields
  if (string.indexOf("y_0") === -1) {
    string = string + " +y_0=0";
  }

  if (string.indexOf("x_0") === -1) {
    string = string + " +x_0=0";
  }

  if (string.indexOf("lon_0") === -1) {
    string = string + " +lon_0=0";
  }

  if (string.indexOf("lat_0") === -1) {
    string = string + " +lat_0=0";
  }

  string = string.split(" +").join(" ");
  string = string.split(" ").join(" +");
  if (string[0] !== "+") {
    string = "+" + string;
  }

  return string;
}

/** @private */
function _proj4Transform(data, proj1, proj2) {
  if (data.isVector3) {
    data = data.toArray();
  }

  if (proj1 === proj2) {
    return new Vector3(...data);
  }

  let projToMeters1 = infoToMeters(proj1);
  let projToMeters2 = infoToMeters(proj2);
  let proj2m = projToMeters2 / projToMeters1;

  let transform = proj4(proj1.string, proj2.string);
  let reprojected = transform.forward(data);
  reprojected = new Vector3(...reprojected);

  // Height scaling is not applied by library
  reprojected.z /= proj2m;

  return reprojected;
}


const LocalScene = new ScenePointConverter();
export default LocalScene;
